import { usesHostedBilling } from '@/lib/appDistribution';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Linking, Platform, View } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useAuthStore, useCreditPacksStore, useModalStore } from '@/store';
import { useBillingPalette } from './billing/BillingGroup';
import Icon from '@/components/Icon';
import { creditPacksApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import AutoTopupEditor, { money, topupStatus } from '@/components/Credits/AutoTopupEditor';
import { AUTO_TOPUP_BONUS_PERCENT, bonusCreditsFor, grantedCreditsFor } from '@/lib/autoTopupOffer';
import { cardLastFour, formatCardBrand } from '@/lib/cardDisplay';

export default function AutoTopUpSettings({ startEditing = false, onClose, inCard = false, compact = false }) {
  const palette = useBillingPalette();
  const user = useAuthStore(s => s.user);
  const balanceCredits = user?.credits?.totalAvailable;
  const update = useAuthStore(s => s.updateAutoTopupSettings);
  const packs = useCreditPacksStore(s => s.creditPacks);
  const fetchPacks = useCreditPacksStore(s => s.fetchCreditPacks);
  const { openModal } = useModalStore(s => s.actions);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(startEditing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    setError('');
    try {
      const response = await creditPacksApi.getAutoTopupSettings();
      setSettings(response.autoTopup); update(response.autoTopup);
      await fetchPacks();
    } catch (_) { setError('Settings could not load. Please try again.'); }
  }, [fetchPacks, update]);
  useEffect(() => { if (usesHostedBilling()) load(); }, [load]);
  if (!usesHostedBilling()) return null;
  const save = async body => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const response = await creditPacksApi.updateAutoTopupSettings(body);
      if (!response.autoTopup) throw new Error('Reload to check whether your settings saved.');
      setSettings(s => ({ ...s, ...response.autoTopup })); update(response.autoTopup);
      setEditing(false); setNotice(body.enabled ? 'Auto top-up settings saved.' : 'Auto top-up is off. A payment already in progress may still complete.');
      if (body.enabled) onClose?.();
    } catch (err) { setError(err?.message || 'Settings could not save. Reload to check their current state.'); }
    finally { setBusy(false); }
  };
  const setup = async () => {
    setBusy(true); setError('');
    try {
      const response = await creditPacksApi.startWhopAutoTopupSetup();
      if (Platform.OS === 'macos') await Linking.openURL(response.checkoutUrl);
      else await WebBrowser.openBrowserAsync(response.checkoutUrl);
      setNotice('After saving your payment method in Whop, reload here to review and authorize auto top-up. No auto top-up is enabled by saving a card.');
    } catch (err) { setError(err?.message || 'Payment-method setup could not open.'); }
    finally { setBusy(false); }
  };
  const whop = settings?.provider === 'whop';
  const available = !whop || settings?.whopAvailable;
  const pending = ['payment_pending', 'fulfillment_pending'].includes(settings?.status);
  const last4 = cardLastFour(user?.valmar?.maskedCardData?.maskedNumber);
  const card = whop ? 'Saved Whop payment method' : last4
    ? `${formatCardBrand(user.valmar.maskedCardData.cardType)} •••• ${last4}` : 'Saved card';
  const granted = settings?.authorization ? grantedCreditsFor(settings.authorization.credits) : null;
  const bonus = settings?.authorization ? bonusCreditsFor(settings.authorization.credits) : 0;
  const actionStyle = { minHeight: 44, borderRadius: 10, borderWidth: 1, borderColor: palette.groupBorder, backgroundColor: palette.tileFill };
  return <View style={[{ gap: 20 }, inCard && { borderWidth: 1, borderColor: palette.groupBorder, borderRadius: 20, padding: compact ? 22 : 26 }]}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
      {inCard && !compact && <View style={{ width: 42, height: 42, borderRadius: 13, backgroundColor: palette.groupFill, alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="arrows-rotate" size={18} color={palette.ink} />
      </View>}
      <View style={{ flex: 1, minWidth: 0, gap: 5 }}>
        <Text size="l" weight="bold">Auto top-up</Text>
        {!editing && <Text size="s" color="secondary" style={{ lineHeight: 20 }}>Refill when you run low. Get {AUTO_TOPUP_BONUS_PERCENT}% extra credits.</Text>}
      </View>
      {settings && <View style={{ maxWidth: '40%', backgroundColor: palette.groupFill, borderWidth: 1, borderColor: palette.groupBorder, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999 }}><Text size="xs" weight="medium">{topupStatus[settings.status] || 'Review required'}</Text></View>}
    </View>
    {!settings && !error && <ActivityIndicator />}
    {!!notice && <View accessibilityRole={safeAccessibilityRole('status')}><Text size="s" color="secondary">{notice}</Text></View>}
    {!!error && <View accessibilityRole="alert"><Text size="s" color="danger">{error}</Text></View>}
    {settings && <>
      {pending && <Text size="s" color="secondary">We’re checking an existing payment. Another automatic charge is blocked until it is resolved. You can still turn auto top-up off.</Text>}
      {!available && <Text size="s" color="secondary">Whop auto top-up is not available yet. One-time credit purchases remain available.</Text>}
      {!settings.hasPaymentMethod && available && <Text size="s" color="secondary">Save a payment method to set up automatic credit purchases.</Text>}
      {editing && available && settings.hasPaymentMethod && !pending && packs.length > 0 ? <AutoTopupEditor key={`${settings.version}:${settings.paymentMethodFingerprint}`} settings={settings} packs={packs} cardLabel={card} busy={busy} balanceCredits={balanceCredits} onSave={save} onDisable={settings.enabled ? () => save({ enabled: false }) : undefined} onCancel={() => { setEditing(false); setError(''); onClose?.(); }} /> : <>
        {settings.authorization && <View style={{ gap: 16 }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 20, padding: 18, borderRadius: 12, backgroundColor: palette.groupFill }}>
            {[
              ['Credit pack', `${granted.toLocaleString()} credits · ${money(settings.authorization.priceCents / 100)}`],
              ['Refill below', `${settings.thresholdCredits.toLocaleString()} credits`],
              ['Monthly limit', settings.monthlySpendLimit === null ? 'No limit' : money(settings.monthlySpendLimit)],
            ].map(([label, value]) => <View key={label} style={{ flexGrow: 1, flexBasis: 140, gap: 6 }}>
              <Text size="xs" color="secondary">{label}</Text><Text size="s" weight="medium">{value}</Text>
            </View>)}
          </View>
          <View style={{ gap: 6 }}>
            {bonus > 0 && <Text size="xs" color="secondary">Includes {bonus.toLocaleString()} bonus credits on the {settings.authorization.credits.toLocaleString()}-credit pack.</Text>}
            <Text size="xs" color="secondary">{money(settings.currentMonthSpend)} spent or reserved this month</Text>
          </View>
        </View>}
        {!settings.authorization && available && <Text size="s" color="secondary" style={{ lineHeight: 20 }}>Choose your credit pack, when to refill, and a monthly spending limit. You can turn it off anytime.</Text>}
        <View style={{ gap: 12, paddingTop: 18, borderTopWidth: 1, borderColor: palette.separator }}>
          <View style={{ flexDirection: compact ? 'column' : 'row', gap: 10, flexWrap: compact ? 'nowrap' : 'wrap' }}>
            {available && settings.hasPaymentMethod && !pending && <Button variant="transparent" style={actionStyle} text={settings.enabled ? 'Manage auto top-up' : 'Set up auto top-up'} onPress={() => setEditing(true)} isDisabled={busy} isFullWidth={compact} />}
            {!settings.hasPaymentMethod && whop && settings.whopSetupAvailable && <Button variant="transparent" style={actionStyle} text="Save payment method with Whop" onPress={setup} isDisabled={busy} isFullWidth={compact} />}
            {!settings.hasPaymentMethod && !whop && <Button variant="transparent" style={actionStyle} text="Buy credits to save a card" onPress={() => openModal('CreditsPurchaseModal')} isFullWidth={compact} />}
            {settings.enabled && <Button variant="transparent" text="Turn off auto top-up" onPress={() => save({ enabled: false })} isDisabled={busy} style={{ minHeight: 44 }} isFullWidth={compact} />}
          </View>
          {!settings.enabled && available && !pending && <Text size="xs" color="secondary">You review and authorize the details before enabling.</Text>}
        </View>
        {settings.enabled && <Text size="xs" color="secondary">Refills start as soon as your balance drops below your minimum, within your monthly limit, or up to 20 times a day if you set no limit. Turning this off does not cancel a payment already in progress.</Text>}
      </>}
    </>}
    {(!!error || (onClose && !editing)) && <View style={{ flexDirection: 'row', gap: 10 }}>{!!error && <Button text="Reload settings" onPress={load} isDisabled={busy} />}{onClose && !editing && <Button text="Done" onPress={onClose} />}</View>}
  </View>;
}
